import React from "react";
import { Box, styled, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CorpLogo, LiLink } from "../../common/commonComps";
import { baseApi } from "../../common/constants";
import { getSid } from "../../common/utils";
import type { Size } from "../../types";

const BoxTop = styled(Box)<{ doubleHigh: string }>(({ doubleHigh }) => ({
    height: `calc(100% - 66px)`,
    width: `100%`,
    borderRadius: `8px`,
    background: `url(${doubleHigh}) no-repeat top left / cover`,
}));

const BoxBottom = styled(Box)(() => ({
    height: 66,
    marginBottom: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    width: `100%`,
    textAlign: `left`,
    position: `absolute`,
    background: `#FFF`,
}));

const TextLine = styled(Box)(() => ({
    fontSize: `15px`,
    fontWeight: `700`,
    color: `#333`,
    paddingTop: `10px`,
}));

const OuterWrap = styled(Box)<{ isDoubleHigh: boolean }>(({ isDoubleHigh }) => ({
    cursor: `pointer`,
    gridRow: isDoubleHigh ? `span 2` : `unset`,
    boxSizing: `border-box`,
    verticalAlign: `top`,
    height: isDoubleHigh ? 166 * 2 + 20 : 166,
}));

const Wrap = styled(Box)(() => ({
    background: `#FFFFFF`,
    textAlign: `center`,
    height: `100%`,
    boxSizing: `border-box`,
    position: `relative`,
    borderRadius: `8px 8px 0 0`,
}));

interface StoreInfo {
    aid: string;
    asid: string;
    cbp?: string;
    was?: string;
    name: string;
}

interface LogoBoxProps {
    size: Size;
    doubleHigh?: boolean | string;
    src?: string;
    was?: string;
    cbp?: string;
    name: string;
    aid: string;
    asid: string;
    logoPrimaryColor?: string;
    storeGuid?: string;
    setStore: (store: StoreInfo) => void;
    // Extra props passed from parent layouts but not used by this component
    length?: number;
    index?: number;
    storeCellsAcross?: number;
    url?: string;
    description?: string;
}

const LogoBox = ({
    size,
    doubleHigh,
    src,
    was,
    cbp,
    name,
    aid,
    asid,
    logoPrimaryColor,
    storeGuid,
    setStore,
}: LogoBoxProps): React.ReactElement => {
    const theme = useTheme();

    const isDoubleHigh = !!doubleHigh && size !== `small`;

    return (
        <OuterWrap {...{ isDoubleHigh }} style={{ visibility: `initial` }}>
            <LiLink
                target="_blank"
                to={`${baseApi}tooffer?aid=${aid}&asid=${asid}&auth=${encodeURIComponent(getSid() ?? ``)}`}
            >
                <Wrap>
                    <CorpLogo
                        logoPrimaryColor={logoPrimaryColor}
                        doubleHigh={doubleHigh}
                        size={size}
                        src={logoPrimaryColor ? `https://s3.amazonaws.com/retailerimages/whLogo/${storeGuid}` : src}
                    />

                    {isDoubleHigh && <BoxTop doubleHigh={doubleHigh as string} />}
                    <BoxBottom>
                        <InfoOutlinedIcon
                            onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                setStore({ aid, asid, cbp, was, name });
                            }}
                            sx={{ position: `absolute`, right: `-2px`, top: `10px`, color: `#999`, fontSize: 20 }}
                        />

                        {cbp && <TextLine sx={{ color: theme.palette.account.accent }}>{cbp}</TextLine>}
                        {was && (
                            <TextLine sx={{ color: `#666`, fontWeight: 400, fontSize: 14, paddingTop: `5px` }}>
                                was {was}
                            </TextLine>
                        )}
                    </BoxBottom>
                </Wrap>
            </LiLink>
        </OuterWrap>
    );
};

export default LogoBox;
